import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { ClipboardList, FileSignature, BadgeCheck, HandCoins, CalendarClock, History, UserPlus } from 'lucide-react';
import StatCard, { StatCardsRow } from '../../components/balances/StatCard';
import ReferralDetailDrawer from '../../components/agent/ReferralDetailDrawer';
import { ClientStageBadge, LeadStageBadge } from '../../components/agent/AgentStageBadge';
import StatusCellWithTooltip from '../../components/clients/StatusCellWithTooltip';
import {
  EmptyState,
  ErrorState,
  HeaderRow,
  LinkButton,
  LoadingState,
  PageHeader,
  PageSubtitle,
  PageTitle,
  Panel,
  PanelHeader,
  PanelTitle,
  PortalPage,
  PrimaryLink,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../../components/agent/AgentPortalUI';
import {
  agentPortalApi,
  AgentClientStage,
  AgentPortalSummary,
  AgentSourceType,
  formatDateTime,
} from '../../services/agentPortalService';
import { formatCurrency, formatDate } from '../../services/agentBalancesService';
import { extractErrorMessage } from '../../utils/errorHandler';

const PanelsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const KindLabel = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const AgentHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AgentPortalSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);

    agentPortalApi
      .getSummary(controller.signal)
      .then(setSummary)
      .catch(err => {
        if (axios.isCancel(err)) return;
        setError(extractErrorMessage(err, 'שגיאה בטעינת הסיכום'));
      });

    return () => controller.abort();
  }, [reloadKey]);

  if (error) {
    return (
      <PortalPage>
        <Panel>
          <ErrorState text={error} onRetry={() => setReloadKey(k => k + 1)} />
        </Panel>
      </PortalPage>
    );
  }

  if (!summary) {
    return (
      <PortalPage>
        <LoadingState />
      </PortalPage>
    );
  }

  const isOrganization = summary.sourceType === AgentSourceType.Organization;

  return (
    <PortalPage>
      <PageHeader>
        <HeaderRow>
          <div>
            <PageTitle>שלום, {summary.sourceName}</PageTitle>
            <PageSubtitle>
              {isOrganization ? 'סקירת ההפניות של הארגון והעמלות שלו' : 'סקירת ההפניות והעמלות שלך'}
            </PageSubtitle>
          </div>
          <PrimaryLink to="/agent/leads/new">
            <UserPlus size={18} />
            ליד חדש
          </PrimaryLink>
        </HeaderRow>
      </PageHeader>

      <StatCardsRow>
        <StatCard
          title="לידים בטיפול"
          value={summary.activeLeadsCount}
          color="var(--primary-color)"
          icon={<ClipboardList size={20} />}
        />
        <StatCard
          title="לקוחות שחתמו"
          value={summary.signedClientsCount}
          color="var(--secondary-color)"
          icon={<FileSignature size={20} />}
        />
        <StatCard
          title="קצבאות שאושרו"
          value={summary.approvedClientsCount}
          color="var(--success-color)"
          icon={<BadgeCheck size={20} />}
        />
        <StatCard
          title="יתרת עמלה לתשלום"
          value={formatCurrency(summary.remaining)}
          color="var(--warning-color)"
          icon={<HandCoins size={20} />}
        />
      </StatCardsRow>

      <PanelsGrid>
        <Panel>
          <PanelHeader>
            <PanelTitle>
              <CalendarClock size={18} />
              ועדות קרובות
            </PanelTitle>
            <LinkButton
              type="button"
              onClick={() => navigate(`/agent/referrals?stage=${AgentClientStage.AwaitingCommittee}`)}
            >
              לכל הממתינים לוועדה
            </LinkButton>
          </PanelHeader>
          {summary.upcomingCommittees.length === 0 ? (
            <EmptyState text="אין ועדות צפויות" />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>לקוח</Th>
                    <Th>מועד הוועדה</Th>
                  </tr>
                </thead>
                <tbody>
                  {summary.upcomingCommittees.map(c => (
                    <Tr
                      key={`${c.clientId}-${c.committeeDate}`}
                      $clickable
                      tabIndex={0}
                      onClick={() => setSelectedClientId(c.clientId)}
                      onKeyDown={e => e.key === 'Enter' && setSelectedClientId(c.clientId)}
                    >
                      <Td>
                        <strong>{c.clientName}</strong>
                      </Td>
                      <Td>{formatDateTime(c.committeeDate)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Panel>

        <Panel>
          <PanelHeader>
            <PanelTitle>
              <History size={18} />
              עדכונים אחרונים
            </PanelTitle>
            <LinkButton type="button" onClick={() => navigate('/agent/referrals')}>
              לכל ההפניות
            </LinkButton>
          </PanelHeader>
          {summary.recentReferrals.length === 0 ? (
            <EmptyState text="עדיין אין הפניות" />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>שם</Th>
                    <Th>סוג</Th>
                    <Th>שלב</Th>
                    <Th>סטטוס</Th>
                    <Th>עודכן</Th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recentReferrals.map(r => (
                    <Tr
                      key={`${r.isClient ? 'c' : 'l'}-${r.id}`}
                      $clickable={r.isClient}
                      tabIndex={r.isClient ? 0 : undefined}
                      onClick={() => r.isClient && setSelectedClientId(r.id)}
                      onKeyDown={e => r.isClient && e.key === 'Enter' && setSelectedClientId(r.id)}
                    >
                      <Td>
                        <strong>{r.fullName}</strong>
                      </Td>
                      <Td>
                        <KindLabel>{r.isClient ? 'לקוח' : 'ליד'}</KindLabel>
                      </Td>
                      <Td>
                        {r.isClient && r.clientStage != null && <ClientStageBadge stage={r.clientStage} />}
                        {!r.isClient && r.leadStage != null && <LeadStageBadge stage={r.leadStage} />}
                      </Td>
                      <Td>
                        <StatusCellWithTooltip
                          align="start"
                          currentStatusName={r.statusName}
                          currentStatusColor={r.statusColor}
                          currentStages={r.currentStages}
                        />
                      </Td>
                      <Td>{formatDate(r.updatedAt)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Panel>
      </PanelsGrid>

      {selectedClientId != null && (
        <ReferralDetailDrawer clientId={selectedClientId} onClose={() => setSelectedClientId(null)} />
      )}
    </PortalPage>
  );
};

export default AgentHomePage;
